/**
 * ตรรกะล้วนของหน้าจอบทบาทระบบ — แยกออกจาก JSX เพื่อให้ทดสอบกฎได้ตรง ๆ
 *
 * แนวเดียวกับ `permissionMatrixView.ts` — สิ่งที่อยู่ที่นี่คือคำถามที่ตอบได้โดยไม่
 * ต้องเรนเดอร์อะไรเลย
 */

import type { RoleAssignment, SetSystemRoleBody } from '../../lib/rbacAdmin/types';
import {
  ADMIN_ROLE_CODE,
  AUDITOR_ROLE_CODE,
  SYSTEM_ROLE_CODES,
  type SystemRoleCode,
} from '../../lib/rbacAdmin/types';

export interface SystemRoleDescriptor {
  readonly code: SystemRoleCode;
  readonly label: string;
  readonly summary: string;
  /**
   * บทบาทนี้ **ตัดสิทธิ์เข้าถึงข้อมูลธุรกิจทั้งหมด** หรือไม่ (D-009)
   *
   * จริงเฉพาะ `ADMIN` และนี่คือสิ่งที่หน้าจอต้องพูดออกมาให้ดัง ไม่ใช่ผลข้างเคียง
   * ที่ผู้ดูแลจะไปค้นพบเอาเองตอนที่พนักงานคนนั้นโทรมาบอกว่าเปิดใบสั่งผลิตไม่ได้
   * แล้ว — `ADMIN` คือบทบาทของผู้คุม config ไม่ใช่ "สิทธิ์สูงสุด" ที่ทำได้ทุกอย่าง
   */
  readonly removesBusinessAccess: boolean;
}

/**
 * สองบทบาทที่หน้าจอเสนอให้มอบได้ — ตามลำดับที่ contract เขียนไว้
 *
 * ชุดที่ **รับได้จริง** คือ `SYSTEM_ROLE_CODES` ฝั่งเซิร์ฟเวอร์ ซึ่งเพิ่มได้และเป็น
 * ผู้ตอบ `422 ROLE_CODE_NOT_RECOGNISED` เมื่อไม่ตรง รายการที่นี่จึงเป็นตัวเลือกบน
 * ฟอร์มเท่านั้น ไม่ใช่ตัวกรองที่ตัดสินแทน
 */
export const SYSTEM_ROLE_DESCRIPTORS: readonly SystemRoleDescriptor[] = [
  {
    code: ADMIN_ROLE_CODE,
    label: 'ผู้ดูแลระบบ (ADMIN)',
    summary:
      'ดูแลสิทธิ์ แผนก และการตั้งค่าระบบ — และ**ไม่มีสิทธิ์เข้าถึงข้อมูลธุรกิจเลย** (D-009)',
    removesBusinessAccess: true,
  },
  {
    code: AUDITOR_ROLE_CODE,
    label: 'ผู้ตรวจสอบ (AUDITOR)',
    summary:
      'ดูข้อมูลธุรกิจได้ข้ามทุกโรงงานเพื่อการตรวจสอบ แต่ดูได้อย่างเดียว ไม่แก้และไม่อนุมัติ',
    removesBusinessAccess: false,
  },
];

/** คำอธิบายของบทบาทที่หน้าจอรู้จัก — `null` เมื่อเซิร์ฟเวอร์ส่งรหัสที่ยังไม่รู้จัก */
export function describeRole(roleCode: string): SystemRoleDescriptor | null {
  return SYSTEM_ROLE_DESCRIPTORS.find((role) => role.code === roleCode) ?? null;
}

/**
 * ชื่อที่อ่านออกของบทบาท — **คืนรหัสดิบเมื่อไม่รู้จัก ไม่ใช่ซ่อนแถวนั้น**
 *
 * ถ้าเซิร์ฟเวอร์เพิ่มบทบาทที่สาม รายการของผู้ที่ถือมันต้องยังปรากฏบนหน้าจอที่มีไว้
 * ตอบคำถามว่า "ตอนนี้ใครถืออะไรอยู่บ้าง" — สิทธิ์ที่หายไปจากหน้าจอเพราะหน้าจอไม่
 * รู้จักชื่อมัน คือสิทธิ์ที่ไม่มีใครทบทวน
 */
export function roleLabel(roleCode: string): string {
  return describeRole(roleCode)?.label ?? roleCode;
}

/** บทบาทนี้ตัดสิทธิ์ธุรกิจหรือไม่ (D-009) — อ่านจากคำอธิบาย ไม่ใช่เดาจากชื่อ */
export function removesBusinessAccess(roleCode: string): boolean {
  return describeRole(roleCode)?.removesBusinessAccess === true;
}

/** หนึ่งคนกับบทบาททั้งหมดที่เขาถืออยู่ — มุมมองสำหรับ **แสดงผล** เท่านั้น */
export interface EmployeeRoleHolder {
  readonly employee_id: string;
  readonly display_name: string;
  readonly roles: readonly RoleAssignment[];
}

/**
 * จัดกลุ่มแถวของ `GET /admin/roles` ให้เป็นคนละหนึ่งแถว — **ที่ชั้นแสดงผลเท่านั้น**
 *
 * parser ตั้งใจไม่ยุบให้ (คนหนึ่งถือสองบทบาทคือกรณีที่การทบทวนสิทธิ์มองหา) ส่วน
 * ตารางบนหน้าจอต้องการหนึ่งแถวต่อคนเพื่อให้กดมอบ/ถอดได้ต่อคน การจัดกลุ่มจึงอยู่
 * ที่นี่ ไม่ใช่ที่ client — และ **ไม่ทิ้งบทบาทใดไปเลย** ทุกบทบาทของคนคนนั้นยังอยู่
 * ครบในแถวเดียวกัน
 *
 * ลำดับเป็นลำดับที่เซิร์ฟเวอร์ส่งมา (`employee_id` แล้ว `role_code`) ไม่เรียงใหม่ —
 * การเรียงซ้ำฝั่งหน้าจอจะทำให้สองฝั่งเลื่อนออกจากกันโดยไม่มีใครเห็น
 */
export function groupRoleAssignments(
  items: readonly RoleAssignment[],
): readonly EmployeeRoleHolder[] {
  const order: string[] = [];
  const byEmployee = new Map<string, { display_name: string; roles: RoleAssignment[] }>();

  for (const item of items) {
    const existing = byEmployee.get(item.employee_id);
    if (existing === undefined) {
      order.push(item.employee_id);
      byEmployee.set(item.employee_id, {
        display_name: item.display_name,
        roles: [item],
      });
      continue;
    }
    existing.roles.push(item);
  }

  return order.map((employeeId) => {
    const entry = byEmployee.get(employeeId)!;
    return {
      employee_id: employeeId,
      display_name: entry.display_name,
      roles: entry.roles,
    };
  });
}

/** คนนี้ถือบทบาทนี้อยู่แล้วหรือยัง — ใช้ตัดสินว่าปุ่มควรเป็น "มอบ" หรือ "ถอด" */
export function holdsRole(holder: EmployeeRoleHolder, roleCode: string): boolean {
  return holder.roles.some((role) => role.role_code === roleCode);
}

/**
 * ประกอบ body ของ `POST /v1/admin/roles` — คืน `null` เมื่อยังประกอบไม่ได้
 *
 * **ด่านเดียวที่ทางเขียนของหน้าจอนี้เดินผ่าน** เหมือนที่ `toSaveBody()` เป็นด่าน
 * ของตารางสิทธิ์ การคืน `null` แทนการ throw ทำให้ผู้เรียกต้องจัดการกรณีนั้นชัดเจน
 * และทำให้เทสต์พิสูจน์ได้ว่า **ไม่มีทางยิงคำขอที่ไม่ระบุตัวคน** โดยไม่ต้องเรนเดอร์
 * หน้าจอเลย
 *
 * รหัสพนักงานถูก `trim()` เพราะมันเป็น business key ที่ **พิมพ์เข้ามาเอง** (D-039)
 * ช่องว่างท้ายรหัสที่ติดมาจากการคัดลอกจะกลายเป็น `404` ที่ผู้ดูแลมองไม่เห็นสาเหตุ
 * — ส่วนรหัสที่เหลือแต่ช่องว่างล้วนไม่ใช่คำขอที่ตั้งใจส่ง
 */
export function toRoleGrantBody(
  employeeId: string,
  roleCode: string,
  granted: boolean,
): SetSystemRoleBody | null {
  const trimmed = employeeId.trim();
  if (trimmed === '' || roleCode === '') {
    return null;
  }
  return { employee_id: trimmed, role_code: roleCode, granted };
}

/** รหัสบทบาททั้งหมดที่ฟอร์มเสนอ — ใช้ร่วมกันระหว่าง view กับเทสต์ */
export const OFFERED_ROLE_CODES: readonly string[] = SYSTEM_ROLE_CODES;
